import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MainLayout from '@/components/layouts/MainLayout';
import PendingMaintenanceList from '@/components/maintenance/PendingMaintenanceList';
import CompletedMaintenanceList from '@/components/maintenance/CompletedMaintenanceList';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import DataMigrationButton from '@/components/maintenance/DataMigrationButton';
import { Card, CardContent } from '@/components/ui/card';

const ScreenMaintenance: React.FC = () => {
  const { isMantenimiento, isAdmin, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showMigrationTool, setShowMigrationTool] = useState(false);
  
  // Obtener parámetros de la URL
  const tabParam = searchParams.get('tab');
  const screenId = searchParams.get('screenId');
  
  // Determinar la pestaña activa basada en los parámetros de URL
  const initialTab = tabParam === 'completed' ? 'completed' : 'pending';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Actualizar la pestaña activa cuando cambien los parámetros de URL
  useEffect(() => {
    if (tabParam === 'completed') {
      setActiveTab('completed');
    } else if (tabParam === 'pending' || !tabParam) {
      setActiveTab('pending');
    }
  }, [tabParam]);

  // Actualizar la URL cuando cambie la pestaña activa
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/screen-maintenance?tab=${value}${screenId ? `&screenId=${screenId}` : ''}`, { replace: true });
  };

  // Verificar si debe mostrar la herramienta de migración (solo para administradores)
  useEffect(() => {
    if (user && isAdmin) {
      // Verificar si hay datos en localStorage para migrar
      const hasPendingData = localStorage.getItem('maintenance_reports') !== null;
      const hasCompletedData = localStorage.getItem('completed_maintenance_reports') !== null;
      setShowMigrationTool(hasPendingData || hasCompletedData);
    } else {
      setShowMigrationTool(false);
    }
  }, [user, isAdmin]);

  // Redirigir si no es encargado de mantenimiento ni administrador
  if (!isMantenimiento && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleMigrationComplete = () => {
    setShowMigrationTool(false);
    // Recargar la página para mostrar los datos migrados
    window.location.reload();
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mantenimiento de Pantallas</h1>
          <p className="text-muted-foreground">
            Gestione el mantenimiento de las pantallas del sistema
          </p>
        </div>

        {showMigrationTool && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium">Migración de Datos</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Se han detectado datos de mantenimiento almacenados localmente. 
                  Para garantizar la persistencia de estos datos, debe migrarlos a la base de datos.
                </p>
                <DataMigrationButton onMigrationComplete={handleMigrationComplete} />
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue={initialTab} value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pending">Tareas Pendientes</TabsTrigger>
            <TabsTrigger value="completed">Historial</TabsTrigger>
          </TabsList>
          
          <TabsContent value="pending" className="mt-6">
            <PendingMaintenanceList />
          </TabsContent>
          
          <TabsContent value="completed" className="mt-6">
            <CompletedMaintenanceList screenId={activeTab === 'completed' ? screenId : undefined} />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ScreenMaintenance; 